import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ROLE_LABEL, can, roleText, useOrg } from "./org";
import { RoleName } from "./RoleNames";

const ScopePicker = ({ scope, setScope, hint }) => {
  const { membership } = useOrg();
  return (
  <div className="mt-4" data-testid="director-scope">
    <p className="t-label">Care oversight</p>
    <div className="flex flex-wrap gap-2 mt-2">
      {[["church", "Whole church"], ["groups", "Chosen Care Groups only"]].map(([v, l]) => (
        <button key={v} aria-pressed={scope === v} onClick={() => setScope(v)} className="choice h-12 px-4" data-testid={`director-scope-${v}`}>{l}</button>
      ))}
    </div>
    <p className="t-meta mt-2">{roleText(membership, "Care Directors coordinate care and assignments. They don't manage settings, roles or integrations, and don't see confidential notes.")}{scope === "groups" && hint ? ` ${hint}` : ""}</p>
  </div>
  );
};

const InviteForm = ({ onSent }) => {
  const { api } = useOrg();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("leader");
  const [scope, setScope] = useState("groups");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api("/invitations", { method: "POST", body: { email, role, director_scope: scope } });
      setLast(r);
      setEmail("");
      toast.success(r.email_sent ? `Invitation emailed to ${r.invitation.email}` : "Invitation created. Email is unavailable, so share the link instead.");
      onSent();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <section className="mt-10" data-testid="invite-section">
      <h2 className="t-heading">Invite someone</h2>
      <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Their email address" className="field h-14 px-4 mt-4" data-testid="invite-email-input" />
      <div className="flex flex-wrap gap-2 mt-3">
        {["pastor", "director", "leader", "admin"].map((r) => (
          <button key={r} aria-pressed={role === r} onClick={() => setRole(r)} className="choice h-12 px-5" data-testid={`invite-role-${r}`}><RoleName role={r} /></button>
        ))}
      </div>
      {role === "director" && <ScopePicker scope={scope} setScope={setScope} hint="You can choose their Care Groups after they join." />}
      <button disabled={!email.includes("@") || busy} onClick={send} className="btn btn-primary w-full mt-5" data-testid="invite-send-btn"><Mail size={18} /> {busy ? "Sending…" : "Send invitation"}</button>
      {last && (
        <button onClick={() => { navigator.clipboard?.writeText(last.invite_link); toast("Invite link copied"); }} className="btn btn-quiet btn-sm mt-3" data-testid="invite-copy-link-btn"><Copy size={16} /> Copy invite link instead</button>
      )}
    </section>
  );
};

const MemberDialog = ({ member, onClose, onSaved }) => {
  const { api, membership } = useOrg();
  const isOwner = can(membership).owner;
  const [role, setRole] = useState(member.role);
  const [conf, setConf] = useState(member.confidential_access);
  const [scope, setScope] = useState(member.director_scope || "groups");
  const [gids, setGids] = useState(new Set(member.director_group_ids || []));
  const [groups, setGroups] = useState([]);
  useEffect(() => { api("/groups").then(setGroups).catch(() => {}); }, [api]);
  const [busy, setBusy] = useState(false);
  const confAllowed = ["owner", "pastor"].includes(role);
  const run = async (path, opts, msg) => {
    setBusy(true);
    try { await api(path, opts); toast.success(msg); onSaved(); onClose(); } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const save = () => {
    const body = { role };
    if (role === "director") Object.assign(body, { director_scope: scope, director_group_ids: [...gids] });
    if (isOwner) body.confidential_access = confAllowed && conf;
    run(`/members/${member.id}`, { method: "PATCH", body }, "Changes saved");
  };
  const paused = member.status === "disabled";
  const roles = isOwner ? ["owner", "admin", "pastor", "director", "leader"] : ["admin", "pastor", "director", "leader"];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-paper max-w-md" data-testid="member-dialog">
        <DialogTitle className="t-name-lg">{member.user?.name}</DialogTitle>
        <DialogDescription className="t-meta">{member.user?.email}</DialogDescription>
        <p className="t-label mt-4">Role</p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {roles.map((r) => (
            <button key={r} aria-pressed={role === r} onClick={() => setRole(r)} className="choice h-12 px-4" data-testid={`member-role-${r}`}><RoleName role={r} /></button>
          ))}
        </div>
        {role === "director" && <>
          <ScopePicker scope={scope} setScope={setScope} />
          {scope === "groups" && (
            <div className="rule-list border-y border-line mt-3 max-h-48 overflow-y-auto" data-testid="director-groups">
              {groups.map((g) => (
                <label key={g.id} className="py-3 flex items-center gap-3 cursor-pointer" data-testid={`director-group-${g.id}`}>
                  <input type="checkbox" className="w-5 h-5 accent-[var(--moss)]" checked={gids.has(g.id)} onChange={() => setGids((s) => { const n = new Set(s); n.has(g.id) ? n.delete(g.id) : n.add(g.id); return n; })} />
                  <span className="t-body">{g.name}</span>
                </label>
              ))}
              {!groups.length && <p className="t-meta py-3">No Care Groups yet.</p>}
            </div>
          )}
        </>}
        <div className="flex items-start justify-between gap-4 mt-6">
          <div><p className="t-body font-semibold">Confidential Access</p><p className="t-meta">{roleText(membership, isOwner ? (confAllowed ? "Can read confidential pastoral notes." : "Only Pastors and Owners can have it.") : "Only the Owner can change this.")}</p></div>
          <Switch checked={confAllowed && conf} disabled={!isOwner || !confAllowed} onCheckedChange={setConf} data-testid="member-confidential-switch" />
        </div>
        <button disabled={busy} onClick={save} className="btn btn-primary w-full mt-6" data-testid="member-save-btn">Save changes</button>
        <div className="flex gap-2 mt-2">
          <button disabled={busy} onClick={() => run(`/members/${member.id}`, { method: "PATCH", body: { status: paused ? "active" : "disabled" } }, paused ? "Access restored" : "Access paused")} className="btn btn-outline btn-sm flex-1" data-testid="member-toggle-status-btn">{paused ? "Restore access" : "Pause access"}</button>
          <button disabled={busy} onClick={() => run(`/members/${member.id}`, { method: "DELETE" }, "Removed from the church")} className="btn btn-outline btn-sm flex-1 text-sun" data-testid="member-remove-btn">Remove</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MemberRow = ({ m, editable, onOpen }) => {
  const Tag = editable ? "button" : "div";
  return (
    <Tag onClick={editable ? onOpen : undefined} className="w-full text-left py-4 flex items-center justify-between gap-3" data-testid={`member-${m.id}`}>
      <div className="min-w-0"><p className="t-name-sm truncate">{m.user?.name}</p><p className="t-meta truncate">{m.user?.email}</p></div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        <span className="status status-neutral" data-testid={`member-rolebadge-${m.id}`}><RoleName role={m.role} />{m.role === "director" ? (m.director_scope === "church" ? " · Whole church" : ` · ${(m.director_group_ids || []).length} Care Groups`) : ""}</span>
        {m.confidential_access && <span className="status status-private text-xs" data-testid={`member-confbadge-${m.id}`}>Confidential</span>}
        {m.status === "disabled" && <span className="status status-attention text-xs" data-testid={`member-pausedbadge-${m.id}`}>Access paused</span>}
      </div>
    </Tag>
  );
};

export default function Team() {
  const { api, membership, onTenantError } = useOrg();
  const c = can(membership);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api("/members").then(setData).catch((e) => { onTenantError(e); toast.error(e.message); });
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const editable = (m) => c.manage && (c.owner || (m.user_id !== membership.user_id && m.role !== "owner" && !m.confidential_access));
  const resend = (id) => api(`/invitations/${id}/resend`, { method: "POST" }).then((r) => { toast[r.email_sent ? "success" : "error"](r.email_sent ? "Invitation emailed again" : "Email is unavailable right now, try again shortly"); load(); }).catch((e) => toast.error(e.message));
  const revoke = (id) => api(`/invitations/${id}`, { method: "DELETE" }).then(() => { toast("Invitation withdrawn"); load(); }).catch((e) => toast.error(e.message));
  return (
    <div data-testid="team-screen">
      <p className="t-label">{membership.org_name}</p>
      <h1 className="t-greeting mt-3">Your care team</h1>
      <p className="t-lead mt-3">{c.manage ? "Invite people and choose what each person can do." : "The people caring for your church."}</p>
      {c.manage && <InviteForm onSent={load} />}
      <section className="mt-12">
        <h2 className="t-heading">People on the team</h2>
        <div className="rule-list border-y border-line mt-4" data-testid="members-list">
          {data?.members.map((m) => <MemberRow key={m.id} m={m} editable={editable(m)} onOpen={() => setOpen(m)} />)}
        </div>
      </section>
      {data?.invitations.length > 0 && (
        <section className="mt-12">
          <h2 className="t-heading">Waiting to join</h2>
          <div className="rule-list border-y border-line mt-4" data-testid="invitations-list">
            {data.invitations.map((i) => (
              <div key={i.id} className="py-4 flex items-center justify-between gap-3" data-testid={`invite-${i.id}`}>
                <div className="min-w-0"><p className="t-name-sm truncate">{i.email}</p><p className="t-meta"><RoleName role={i.role} /> · {i.email_sent === false ? "Email not sent" : "Invitation emailed"}</p></div>
                {c.manage && (
                  <div className="flex gap-2 shrink-0">
                    {i.email_sent === false && <button onClick={() => resend(i.id)} className="btn btn-outline btn-sm" data-testid={`invite-resend-${i.id}`}>Resend</button>}
                    <button onClick={() => revoke(i.id)} className="btn btn-quiet btn-sm" data-testid={`invite-revoke-${i.id}`}>Withdraw</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
      {open && <MemberDialog member={open} onClose={() => setOpen(null)} onSaved={load} />}
    </div>
  );
}
